import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, FileSearch, Sparkles, Eye, Network, BookOpen,
  Library, Lock, MessageSquare, Sigma,
  Users, Crown, Gamepad2, Layers, ArrowDown, Shuffle, Search, Grid3X3, Link2, Flame,
  Globe, MessagesSquare, ChevronsDown, MapPin, Clock, Skull,
  Hash, Calculator, Languages, AtSign, UserPlus, Image as ImageIcon, MailX,
  Pin, GitBranch, FileText, Mic, Dice5, Award, Repeat, Trophy, ShieldCheck, KeyRound, Megaphone, IdCard, Snowflake,
} from "lucide-react";
import { fetchCategories, fetchPublicCounts, fetchDailyFind, fetchDossiers, fetchPublishedDeepDives, fetchPublishedConnections } from "../lib/api";
import DailyPattern from "../components/DailyPattern";
import useLiveViewers from "../hooks/useLiveViewers";
import { useTrackPageView } from "../hooks/useAnalytics";
import { useAuth } from "../lib/auth";
import { isAdmin } from "../lib/admin";
import VaultRouletteButton from "../components/VaultRouletteButton";
import HomeGlitchCountdowns from "../components/HomeGlitchCountdowns";
import DeadDrop from "../components/DeadDrop";
import ArgCrackTheCode from "../components/ArgCrackTheCode";

// Deep cosmic nebula — pairs with the "alien / consciousness / hidden files" thesis
const HERO_BG =
  "https://images.unsplash.com/photo-1462331940025-496dfbfc7564?crop=entropy&cs=srgb&fm=jpg&w=2400&q=80";

export default function HomePage() {
  useTrackPageView("/");
  const { user } = useAuth();
  const [cats, setCats] = useState([]);
  const [counts, setCounts] = useState({ dossiers: 0, deep_dives: 0, connections: 0, total: 0 });
  const [daily, setDaily] = useState(null);
  const [now, setNow] = useState(new Date());
  const viewers = useLiveViewers();
  const heroRef = useRef(null);
  const nebulaRef = useRef(null);

  // Suppress user theme bg on the homepage only.
  useEffect(() => {
    document.body.setAttribute("data-home-page", "1");
    return () => document.body.removeAttribute("data-home-page");
  }, []);

  // Lifelike mouse-parallax — clouds drift toward / away from cursor by a few pixels.
  // Throttled with requestAnimationFrame so it stays buttery on every device.
  useEffect(() => {
    const hero = heroRef.current;
    const nebula = nebulaRef.current;
    if (!hero || !nebula) return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let targetX = 0, targetY = 0, currentX = 0, currentY = 0, raf = 0;
    const MAX = 18; // pixels of drift at the edges — subtle, lifelike

    const onMove = (e) => {
      const rect = hero.getBoundingClientRect();
      const cx = (e.clientX - rect.left) / rect.width - 0.5;   // -0.5 .. 0.5
      const cy = (e.clientY - rect.top) / rect.height - 0.5;
      targetX = -cx * MAX; // invert for natural "looking-into-depth" feel
      targetY = -cy * MAX;
    };
    const onLeave = () => { targetX = 0; targetY = 0; };

    const tick = () => {
      // Ease toward target — gives the cloud weight, not snap-tracking
      currentX += (targetX - currentX) * 0.04;
      currentY += (targetY - currentY) * 0.04;
      nebula.style.setProperty("--neb-px", `${currentX.toFixed(2)}px`);
      nebula.style.setProperty("--neb-py", `${currentY.toFixed(2)}px`);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    hero.addEventListener("mousemove", onMove);
    hero.addEventListener("mouseleave", onLeave);
    return () => {
      cancelAnimationFrame(raf);
      hero.removeEventListener("mousemove", onMove);
      hero.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  const [archiveItems, setArchiveItems] = useState([]);  // archive pool — kept for potential reuse, no longer drives a hero rotator

  useEffect(() => {
    fetchCategories().then(setCats).catch(() => {});
    fetchPublicCounts().then(setCounts).catch(() => {});
    fetchDailyFind().then(setDaily).catch(() => {});
    // Pull a slice of the archive — still surfaced via other tiles on the page.
    Promise.all([
      fetchDossiers({ limit: 50 }).catch(() => []),
      fetchPublishedDeepDives().catch(() => []),
      fetchPublishedConnections().catch(() => []),
    ]).then(([curated, dives, conns]) => {
      const normalized = [
        ...((curated || []).map((d) => ({
          kind: "dossier", id: d.slug, slug: d.slug, title: d.title,
          category: d.category, summary: d.summary, route: `/dossier/${d.slug}`,
        }))),
        ...((dives || []).map((d) => ({
          kind: "ai_dossier", id: d.id, title: d.topic,
          category: "ai_compiled", summary: (d.report || "").slice(0, 220),
          route: `/ai-dossier/${d.id}`,
        }))),
        ...((conns || []).map((c) => ({
          kind: "connection", id: c.id, title: c.title,
          category: "connection", summary: (c.analysis || "").slice(0, 220),
          route: `/connection/${c.id}`,
        }))),
      ];
      // Shuffle so AI / connections aren't always at the bottom of the rotation.
      // Fisher-Yates.
      for (let i = normalized.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [normalized[i], normalized[j]] = [normalized[j], normalized[i]];
      }
      setArchiveItems(normalized);
    });
    const refresh = setInterval(() => {
      fetchPublicCounts().then(setCounts).catch(() => {});
    }, 30000);
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => {
      clearInterval(t);
      clearInterval(refresh);
    };
  }, []);

  // (Removed 2026-02) — hero "From the Archive" 6s rotator. Frequency Index
  // sits where this used to. Keeping the archiveItems pool above for any
  // future tile that needs a recent-archive sample.

  const stamp = now.toISOString().replace("T", " // ").slice(0, 19) + " UTC";
  const totalFiles = counts.dossiers || cats.reduce((n, c) => n + (c.count || 0), 0);

  return (
    <div data-testid="home-page">
      {/* ============== HERO ============== */}
      <section ref={heroRef} className="relative min-h-[88vh] overflow-hidden border-b border-white/10">
        {/* Slow zoom on the cosmic backplate so the hero feels alive */}
        <div
          className="absolute inset-0 bg-cover bg-center hero-cosmic-drift"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        {/* Lighter dim — let the nebula colors actually show through */}
        <div className="absolute inset-0 bg-[#050505]/55" />
        {/* Multi-color nebula cloud blobs that pulse in/out */}
        <div ref={nebulaRef} className="absolute inset-0 hero-nebula-clouds pointer-events-none" />
        {/* Animated parallax twinkle field over the image */}
        <div className="absolute inset-0 hero-starfield pointer-events-none" />
        {/* Shooting stars that streak diagonally every few seconds */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <span className="shooting-star shooting-star-1" />
          <span className="shooting-star shooting-star-2" />
          <span className="shooting-star shooting-star-3" />
        </div>
        {/* Roaming UFO — patrols across the cosmos on a curved flight path */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
          <div className="ufo-craft" data-testid="hero-ufo">
            <svg viewBox="0 0 80 32" width="80" height="32" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <radialGradient id="ufoBeam" cx="50%" cy="0%" r="65%">
                  <stop offset="0%" stopColor="#00ff88" stopOpacity="0.85" />
                  <stop offset="60%" stopColor="#00ff88" stopOpacity="0.18" />
                  <stop offset="100%" stopColor="#00ff88" stopOpacity="0" />
                </radialGradient>
                <radialGradient id="ufoDome" cx="50%" cy="40%" r="60%">
                  <stop offset="0%" stopColor="#a78bfa" stopOpacity="1" />
                  <stop offset="100%" stopColor="#5b21b6" stopOpacity="0.6" />
                </radialGradient>
              </defs>
              {/* Beam */}
              <path d="M28 18 L52 18 L62 32 L18 32 Z" fill="url(#ufoBeam)" />
              {/* Saucer body */}
              <ellipse cx="40" cy="18" rx="22" ry="4" fill="#1a1a1a" stroke="#a78bfa" strokeWidth="0.6" />
              <ellipse cx="40" cy="17" rx="14" ry="6" fill="url(#ufoDome)" stroke="#a78bfa" strokeWidth="0.5" />
              {/* Lights */}
              <circle cx="22" cy="19" r="1.2" fill="#ff3b30" className="ufo-light-1" />
              <circle cx="34" cy="19" r="1.2" fill="#ffd60a" className="ufo-light-2" />
              <circle cx="46" cy="19" r="1.2" fill="#00ff88" className="ufo-light-3" />
              <circle cx="58" cy="19" r="1.2" fill="#60a5fa" className="ufo-light-4" />
            </svg>
          </div>

          {/* === Abduction UFO easter egg — UFO + beam + doc as ONE rigid scene so they always line up === */}
          <div
            className="abduction-scene"
            data-testid="hero-abduction-scene"
            aria-hidden="true"
          >
            {/* The saucer at the top of the scene (z-stack 3) */}
            <div className="abduction-ufo">
              <svg viewBox="0 0 80 32" width="80" height="32" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <radialGradient id="abductDome" cx="50%" cy="40%" r="60%">
                    <stop offset="0%" stopColor="#00ff88" stopOpacity="1" />
                    <stop offset="100%" stopColor="#0d6b3a" stopOpacity="0.7" />
                  </radialGradient>
                </defs>
                <ellipse cx="40" cy="18" rx="22" ry="4" fill="#0a0a0a" stroke="#00ff88" strokeWidth="0.7" />
                <ellipse cx="40" cy="17" rx="14" ry="6" fill="url(#abductDome)" stroke="#00ff88" strokeWidth="0.5" />
                <circle cx="22" cy="19" r="1.2" fill="#00ff88" className="ufo-light-1" />
                <circle cx="34" cy="19" r="1.2" fill="#00ff88" className="ufo-light-2" />
                <circle cx="46" cy="19" r="1.2" fill="#00ff88" className="ufo-light-3" />
                <circle cx="58" cy="19" r="1.2" fill="#00ff88" className="ufo-light-4" />
              </svg>
            </div>
            {/* Tractor beam — anchored to the bottom-center of the saucer */}
            <div className="abduction-beam" />
            {/* Top-secret document — starts at the BOTTOM of the beam, rises into the saucer */}
            <svg
              className="abduction-doc"
              data-testid="hero-abduct-doc"
              viewBox="0 0 28 36"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect x="2" y="2" width="24" height="32" fill="#f5f5f5" stroke="#a78bfa" strokeWidth="0.6" />
              <rect x="2" y="2" width="24" height="6" fill="#ff3b30" />
              <line x1="6" y1="14" x2="22" y2="14" stroke="#1a1a1a" strokeWidth="0.6" />
              <line x1="6" y1="18" x2="22" y2="18" stroke="#1a1a1a" strokeWidth="0.6" />
              <line x1="6" y1="22" x2="18" y2="22" stroke="#1a1a1a" strokeWidth="0.6" />
              <rect x="6" y="26" width="10" height="3" fill="#1a1a1a" />
              <rect x="18" y="26" width="4" height="3" fill="#1a1a1a" />
            </svg>
          </div>
        </div>
        {/* Center vignette ONLY behind the text — softer everywhere else */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse 60% 70% at 30% 50%, rgba(5,5,5,0.6) 0%, rgba(5,5,5,0.0) 70%)" }}
        />
        <div className="absolute inset-0 scanlines pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 pt-6 pb-24">
          {/* Top metadata bar — sits right under the navbar */}
          <div className="flex items-center justify-between font-mono text-[10px] sm:text-xs tracking-[0.25em] font-bold text-[#050505] border-b border-[#050505] pb-3 mb-8">
            <span>V. JONES // ARCHIVIST</span>
            <span className="hidden sm:inline">CLEARANCE: PUBLIC</span>
            <span data-testid="hero-timestamp">{stamp}</span>
          </div>

          <div className="max-w-5xl">
          {/* Public "LIVE — INTAKE ACTIVE" pill removed. Admins still see the
              live viewer count here as a private heartbeat — non-admins never
              see this. */}
          {isAdmin() && viewers !== null && viewers > 0 && (
            <div
              className="inline-flex items-center gap-2 border border-[#ff3b30]/40 bg-[#ff3b30]/5 px-3 py-1.5 mb-5"
              data-testid="live-viewers-hero"
              title="Admin only: site viewers right now"
            >
              <span className="w-1.5 h-1.5 bg-[#ff3b30] rounded-full animate-pulse" />
              <span className="font-mono text-[10px] sm:text-xs tracking-[0.25em] text-[#ff3b30] flex items-center gap-1">
                <Eye size={11} /> {viewers} VIEWING
              </span>
            </div>
          )}

            <h1 className="font-heading font-black uppercase tracking-tighter text-4xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.92] text-white">
              The Deep <br /> Archive of <br />
              <span className="text-[#ff3b30]">Hidden Files</span>
              <span className="caret" />
            </h1>

            {/* Live stats strip */}
            <div className="mt-6 grid grid-cols-3 sm:flex sm:flex-wrap gap-4 sm:gap-10 font-mono text-[10px] sm:text-xs tracking-[0.25em] font-bold text-[#050505] border-y border-[#050505] py-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#ff3b30] rounded-full animate-pulse" />
                  <div className="text-2xl sm:text-3xl font-heading font-black text-[#ff3b30] tracking-tight">
                    {counts.archive_total ?? counts.total ?? "—"}
                  </div>
                </div>
                <div className="mt-0.5">CURATED FILES</div>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#00ff88] rounded-full animate-pulse" />
                  <div className="text-2xl sm:text-3xl font-heading font-black text-[#00ff88] tracking-tight">
                    {counts.deep_dives || 0}
                  </div>
                </div>
                <div className="mt-0.5">AI DEEP DIVES</div>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-pink-400 rounded-full animate-pulse" />
                  <div className="text-2xl sm:text-3xl font-heading font-black text-pink-400 tracking-tight">
                    {counts.connections || 0}
                  </div>
                </div>
                <div className="mt-0.5">CONNECTIONS</div>
              </div>
            </div>

            {/* Hero CTAs — uniform grid, in navbar order, with Random last.
                Stacks responsively: 2 cols mobile → 3 cols tablet → 5 cols desktop. */}
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              <Link
                to="/archive"
                data-testid="hero-enter-archive"
                className="inline-flex items-center justify-center gap-2 bg-transparent text-[#ff3b30] border border-[#ff3b30]/50 hover:bg-[#ff3b30] hover:text-white px-3 py-3 font-mono text-[10px] sm:text-xs uppercase tracking-[0.18em] transition-colors whitespace-nowrap"
              >
                Archive <ArrowRight size={13} />
              </Link>
              <Link
                to="/deep-dive"
                data-testid="hero-deep-dive"
                className="inline-flex items-center justify-center gap-2 bg-transparent text-[#00ff88] border border-[#00ff88]/50 hover:bg-[#00ff88] hover:text-[#050505] px-3 py-3 font-mono text-[10px] sm:text-xs uppercase tracking-[0.18em] transition-colors whitespace-nowrap"
              >
                <Sparkles size={13} /> Deep Dive
              </Link>
              <Link
                to="/connect-dots"
                data-testid="hero-connect-dots"
                className="inline-flex items-center justify-center gap-2 bg-transparent text-pink-300 border border-pink-500/50 hover:bg-pink-400 hover:text-[#050505] px-3 py-3 font-mono text-[10px] sm:text-xs uppercase tracking-[0.18em] transition-colors whitespace-nowrap"
              >
                <Network size={13} /> Connect Dots
              </Link>
              <Link
                to="/equation"
                data-testid="hero-equation"
                className="inline-flex items-center justify-center gap-2 bg-transparent text-[#ffd60a] border border-[#ffd60a]/50 hover:bg-[#ffd60a] hover:text-[#050505] px-3 py-3 font-mono text-[10px] sm:text-xs uppercase tracking-[0.18em] transition-colors whitespace-nowrap"
              >
                <Sigma size={13} /> Equation
              </Link>
              <Link
                to="/threads"
                data-testid="hero-threads"
                className="inline-flex items-center justify-center gap-2 bg-transparent text-[#60a5fa] border border-[#60a5fa]/50 hover:bg-[#60a5fa] hover:text-[#050505] px-3 py-3 font-mono text-[10px] sm:text-xs uppercase tracking-[0.18em] transition-colors whitespace-nowrap"
              >
                <MessageSquare size={13} /> Threads
              </Link>
              <Link
                to="/warroom"
                data-testid="hero-warroom"
                className="inline-flex items-center justify-center gap-2 bg-transparent text-[#22d3ee] border border-[#22d3ee]/50 hover:bg-[#22d3ee] hover:text-[#050505] px-3 py-3 font-mono text-[10px] sm:text-xs uppercase tracking-[0.18em] transition-colors whitespace-nowrap"
              >
                <Users size={13} /> The Ties
              </Link>
              <Link
                to="/vault-cards"
                data-testid="hero-vault-cards"
                className="inline-flex items-center justify-center gap-2 bg-transparent text-[#ff9500] border border-[#ff9500]/50 hover:bg-[#ff9500] hover:text-[#050505] px-3 py-3 font-mono text-[10px] sm:text-xs uppercase tracking-[0.18em] transition-colors whitespace-nowrap"
              >
                <Layers size={13} /> Cards
              </Link>
              <Link
                to="/awakening"
                data-testid="hero-awakening"
                className="inline-flex items-center justify-center gap-2 bg-transparent text-[#fde047] border border-[#fde047]/50 hover:bg-[#fde047] hover:text-[#050505] px-3 py-3 font-mono text-[10px] sm:text-xs uppercase tracking-[0.18em] transition-colors whitespace-nowrap"
              >
                <Gamepad2 size={13} /> Awakening
              </Link>
              <Link
                to="/profile"
                data-testid="hero-profile"
                className="inline-flex items-center justify-center gap-2 bg-transparent text-[#a78bfa] border border-[#a78bfa]/50 hover:bg-[#a78bfa] hover:text-[#050505] px-3 py-3 font-mono text-[10px] sm:text-xs uppercase tracking-[0.18em] transition-colors whitespace-nowrap"
              >
                <Crown size={13} /> Profile
              </Link>
              <VaultRouletteButton variant="grid" />
            </div>
            <HomeGlitchCountdowns />
          </div>
        </div>
      </section>

      {/* ============== DAILY DECLASSIFIED FIND ============== */}
      {daily && (
        <section data-testid="daily-find-banner" className="border-b border-white/10 bg-[#070707]">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
            <div className="flex-shrink-0 inline-flex items-center gap-2 border border-[#ffd60a]/40 bg-[#ffd60a]/5 px-3 py-1.5">
              <span className="w-1.5 h-1.5 bg-[#ffd60a] rounded-full animate-pulse" />
              <span className="font-mono text-[10px] tracking-[0.25em] text-[#ffd60a]">
                DAILY DECLASSIFIED FIND
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <Link
                to={daily.slug ? `/dossier/${daily.slug}` : "/archive"}
                data-testid="daily-find-link"
                className="group block"
              >
                <h3 className="font-heading font-black uppercase tracking-tight text-xl sm:text-2xl text-white group-hover:text-[#ffd60a] transition-colors leading-tight">
                  {daily.title}
                </h3>
                {daily.summary && (
                  <p className="mt-2 text-sm text-zinc-400 line-clamp-2">{daily.summary}</p>
                )}
              </Link>
            </div>
            <div className="flex-shrink-0 font-mono text-[10px] tracking-[0.25em] text-zinc-500">
              {(daily.credibility || "").toUpperCase()} · {daily.date || ""}
            </div>
          </div>
        </section>
      )}

      {/* ============== AI PATTERN OF THE DAY ============== */}
      <section className="border-b border-white/10 bg-[#070707]" data-testid="daily-pattern-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <DailyPattern />
        </div>
      </section>

      {/* ============== LIVING VAULT — Crack the Code · The Dead Drop · Live Sigil Wall ============== */}
      {/* The three interactive blocks that make the homepage feel alive every visit:
         - Crack the Code: DAILY cipher puzzle rotating at UTC midnight, random Vault Card reward
         - The Dead Drop: DAILY Semantle-style hot/cold word hunt — solve to auto-mint mythic+ pull
         - Sigil Wall: rolling ticker of community unlocks */}
      <section className="border-b border-white/10 bg-[#050505]" data-testid="living-vault-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16">
          <div className="max-w-4xl mb-10">
            <div className="font-mono text-[10px] tracking-[0.3em] text-[#ffd60a] mb-3 flex items-center gap-2">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#00ff88] animate-pulse" />
              // THE LIVING VAULT — PLAY DAILY, PULL DAILY
            </div>
            <h2 className="font-heading font-black uppercase tracking-tighter text-3xl sm:text-5xl text-white leading-[0.95]">
              The Vault is alive.<br />
              <span className="text-zinc-500">It rewards the curious.</span>
            </h2>
          </div>
          <div className="grid lg:grid-cols-2 gap-5 mb-5">
            <ArgCrackTheCode />
            <DeadDrop />
          </div>
        </div>
      </section>

      {/* ============== WHAT'S INSIDE — 4 PILLARS ============== */}
      <section className="border-b border-white/10 bg-[#070707]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-24">
          <div className="max-w-4xl mb-16">
            <h2 className="font-heading font-black uppercase tracking-tighter text-4xl sm:text-6xl lg:text-7xl text-white leading-[0.9] mb-8">
              Built To <span className="text-[#ff3b30]">Investigate.</span><br />
              Built To <span className="text-[#00ff88]">Remember.</span>
            </h2>
            <p className="text-zinc-300 leading-relaxed text-base sm:text-lg">
              The Vault is a working investigative reading room — not a hot-take feed. Every dossier is
              sourced, dated, and credibility-tiered, then opened up to <span className="text-white">your</span>
              own questions: spin up an AI Deep Dive, map a Connect-the-Dots case, pin findings to a
              real-time Ties whiteboard, or argue it out on Threads. Collectible Vault Cards drop from
              every file you read, every dive you publish, every puzzle you solve — earn coins, level up
              your archivist rank, customize your own corner of the vault. No email collected. No
              tracking. Anonymous works forever.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border border-white/10">
            {/* ARCHIVE — slot 01 · the curated library */}
            <Link
              to="/archive"
              data-testid="pillar-archive"
              className="group flex flex-col p-8 lg:p-10 border-b border-r border-white/10 bg-[#0F0F0F] hover:bg-[#141414] transition-colors relative overflow-hidden"
            >
              <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full border border-[#ff3b30]/20 opacity-40 group-hover:opacity-70 group-hover:scale-110 transition-all duration-500 pointer-events-none" />
              <div className="absolute -right-12 -top-12 w-52 h-52 rounded-full border border-[#ff3b30]/15 opacity-30 group-hover:opacity-60 pointer-events-none" />

              <div className="flex items-center justify-between mb-6 relative">
                <div className="font-mono text-[10px] tracking-[0.25em] text-[#ff3b30]">01 / ARCHIVE</div>
                <Library size={18} className="text-[#ff3b30]" />
              </div>
              <h3 className="font-heading font-black uppercase tracking-tight text-2xl text-white mb-3 group-hover:text-[#ff3b30] transition-colors relative">
                The Curated Library
              </h3>
              <p className="text-sm text-zinc-400 leading-relaxed mb-5 relative">
                One searchable home for everything in the Vault. Hand-built dossiers
                sit side-by-side with everything you publish — every piece sourced,
                credibility-scored, and bookmarkable.
              </p>

              <ul className="space-y-2.5 mb-5 relative" data-testid="archive-feature-list">
                <GameLine icon={FileText}      color="#ff3b30" label="Hand-built dossiers" desc="Hidden history, declassified docs, and FOIA receipts curated by V. Jones." />
                <GameLine icon={Sparkles}      color="#ff3b30" label="Published AI dives"   desc="Every dive any user has decided to share — readable, sourced, and citable." />
                <GameLine icon={Network}       color="#ff3b30" label="Closed connections"   desc="Crossreferences mapped by Connect Dots, Question Mode, and the Triangulator." />
                <GameLine icon={Users}         color="#ff3b30" label="Solved boards"        desc="Finished investigations from The Ties — published once the case is closed." />
                <GameLine icon={Crown}         color="#ff3b30" label="Topic Constellation"  desc="Pan and zoom a starfield of every file. The whole archive as one map." />
              </ul>

              <HighlightBox icon={Megaphone} color="#ff3b30" label="Publish anything"
                body={<>Every dive, connection, board, or closed case you build on the site can be published here — citable in Threads or mintable as an Artifact Card.</>} />

              <div className="pt-2 font-mono text-xs uppercase tracking-[0.2em] text-[#ff3b30] inline-flex items-center gap-2 group-hover:text-white relative">
                Browse {counts.archive_total ?? counts.total ?? totalFiles ?? ""} files <ArrowRight size={12} />
              </div>
            </Link>

            {/* DEEP DIVE — slot 02 · the research engine */}
            <Link
              to="/deep-dive"
              data-testid="pillar-deep-dive"
              className="group flex flex-col p-8 lg:p-10 border-b lg:border-r border-white/10 bg-[#0F0F0F] hover:bg-[#141414] transition-colors relative overflow-hidden"
            >
              <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full border border-[#00ff88]/20 opacity-40 group-hover:opacity-70 group-hover:scale-110 transition-all duration-500 pointer-events-none" />
              <div className="absolute -right-12 -top-12 w-52 h-52 rounded-full border border-[#00ff88]/15 opacity-30 group-hover:opacity-60 pointer-events-none" />

              <div className="flex items-center justify-between mb-6 relative">
                <div className="font-mono text-[10px] tracking-[0.25em] text-[#00ff88]">02 / AI DEEP DIVE</div>
                <Sparkles size={18} className="text-[#00ff88]" />
              </div>
              <h3 className="font-heading font-black uppercase tracking-tight text-2xl text-white mb-3 group-hover:text-[#00ff88] transition-colors relative">
                The Research Engine
              </h3>
              <p className="text-sm text-zinc-400 leading-relaxed mb-5 relative">
                Don&apos;t see your topic in the Archive? Spin up an agent on any subject —
                claims, counter-evidence, timeline, and primary sources, all delivered in seconds.
              </p>

              <ul className="space-y-2.5 mb-5 relative" data-testid="deepdive-feature-list">
                <GameLine icon={Sparkles}      color="#00ff88" label="4 modes"           desc="Standard · Skeptical · ELI5 · Social — same topic, four lenses" />
                <GameLine icon={MessagesSquare} color="#00ff88" label="Continuous chat"  desc="Keep drilling with follow-ups · saved per dive" />
                <GameLine icon={FileSearch}    color="#00ff88" label="Question Mode"    desc="AI generates the questions you should be asking about any topic" />
                <GameLine icon={Globe}         color="#00ff88" label="URL Translator"   desc="Paste any foreign-language link · get a readable English dive" />
                <GameLine icon={ChevronsDown}  color="#00ff88" label="Expand 5 deep"    desc="Roll the dossier 5 levels deeper · audit + undo built in" />
                <GameLine icon={Megaphone}     color="#00ff88" label="Publish to Vault" desc="One click moves your dive into the public Archive" />
              </ul>

              <HighlightBox icon={Lock} color="#00ff88" label="Auto credibility scoring"
                body={<>Every claim ships with a source tier (Declassified → Mainstream → Independent → Unverified) so readers can weigh it instantly.</>} />

              <div className="pt-2 font-mono text-xs uppercase tracking-[0.2em] text-[#00ff88] inline-flex items-center gap-2 group-hover:text-white relative">
                Open the engine <ArrowRight size={12} />
              </div>
            </Link>

            {/* CONNECT DOTS — slot 03 · the crossreference */}
            <Link
              to="/connect-dots"
              data-testid="pillar-connect-dots"
              className="group flex flex-col p-8 lg:p-10 border-b md:border-r border-white/10 bg-[#0F0F0F] hover:bg-[#141414] transition-colors relative overflow-hidden"
            >
              <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full border border-pink-400/20 opacity-40 group-hover:opacity-70 group-hover:scale-110 transition-all duration-500 pointer-events-none" />
              <div className="absolute -right-12 -top-12 w-52 h-52 rounded-full border border-pink-400/15 opacity-30 group-hover:opacity-60 pointer-events-none" />

              <div className="flex items-center justify-between mb-6 relative">
                <div className="font-mono text-[10px] tracking-[0.25em] text-pink-400">03 / CONNECT THE DOTS</div>
                <Network size={18} className="text-pink-400" />
              </div>
              <h3 className="font-heading font-black uppercase tracking-tight text-2xl text-white mb-3 group-hover:text-pink-400 transition-colors relative">
                The Crossreference
              </h3>
              <p className="text-sm text-zinc-400 leading-relaxed mb-5 relative">
                Drop in any people, programs, or dates and the analyst maps every
                documented link between them. One click flips the result into a live Ties board.
              </p>

              <ul className="space-y-2.5 mb-5 relative" data-testid="connect-feature-list">
                <GameLine icon={Network}  color="#f472b6" label="6 analysis modes" desc="Default · Geographic · Skeptic · Timeline · Occult · Bloodline" />
                <GameLine icon={MapPin}   color="#f472b6" label="Fraction Mode"    desc="Dedicated per-side input bars — build rival camps cleanly" />
                <GameLine icon={Globe}    color="#f472b6" label="URL Triangulator" desc="Paste 2–6 sources · agreement / disagreement / credibility matrix" />
                <GameLine icon={Sparkles} color="#f472b6" label="Strongest-link card" desc="Auto-mints a share-ready connection card on each save" />
                <GameLine icon={Users}    color="#f472b6" label="One-click Ties"   desc="Send the result straight to a collaborative Ties board" />
              </ul>

              <HighlightBox icon={Megaphone} color="#f472b6" label="Save or publish"
                body={<>Keep the dossier private in your library, or publish it so others can build on the crossreference you just mapped.</>} />

              <div className="pt-2 font-mono text-xs uppercase tracking-[0.2em] text-pink-400 inline-flex items-center gap-2 group-hover:text-white relative">
                Map a connection <ArrowRight size={12} />
              </div>
            </Link>

            {/* EQUATION BUILDER — slot 04 · clean math */}
            <Link
              to="/equation"
              data-testid="pillar-equation"
              className="group flex flex-col p-8 lg:p-10 border-b md:border-r border-white/10 bg-[#0F0F0F] hover:bg-[#141414] transition-colors relative overflow-hidden"
            >
              <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full border border-[#ffd60a]/20 opacity-40 group-hover:opacity-70 group-hover:scale-110 transition-all duration-500 pointer-events-none" />
              <div className="absolute -right-12 -top-12 w-52 h-52 rounded-full border border-[#ffd60a]/15 opacity-30 group-hover:opacity-60 pointer-events-none" />

              <div className="flex items-center justify-between mb-6 relative">
                <div className="font-mono text-[10px] tracking-[0.25em] text-[#ffd60a]">04 / EQUATION BUILDER</div>
                <Sigma size={18} className="text-[#ffd60a]" />
              </div>
              <h3 className="font-heading font-black uppercase tracking-tight text-2xl text-white mb-3 group-hover:text-[#ffd60a] transition-colors relative">
                Build Clean Math
              </h3>
              <p className="text-sm text-zinc-400 leading-relaxed mb-5 relative">
                Two flavors in one tool. Drop paste-anywhere Unicode symbols, or render
                real stacked fractions and proper sub/superscripts with a live preview.
              </p>

              <ul className="space-y-2.5 mb-5 relative" data-testid="equation-feature-list">
                <GameLine icon={Hash}       color="#ffd60a" label="1000+ symbols"     desc="25 palettes · live search · keyboard-friendly" />
                <GameLine icon={Calculator} color="#ffd60a" label="Tensor builder"    desc="μ ν ρ indices · vector / matrix shortcuts" />
                <GameLine icon={Sigma}      color="#ffd60a" label="Fraction builder"  desc="Real stacked numerator / denominator in LaTeX" />
                <GameLine icon={Languages}  color="#ffd60a" label="LaTeX → Unicode"   desc="Paste LaTeX, get pasteable Unicode out" />
                <GameLine icon={Sparkles}   color="#ffd60a" label="Decorated alphabet" desc="Greek · double-line 𝕥 · fancy ℛ · currency · emoji" />
              </ul>

              <HighlightBox icon={Sparkles} color="#ffd60a" label="Paste anywhere"
                body={<>Unicode output drops cleanly into Notes, Threads, email, Slack — anywhere your math needs to live, no plugins or rendering required.</>} />

              <div className="pt-2 font-mono text-xs uppercase tracking-[0.2em] text-[#ffd60a] inline-flex items-center gap-2 group-hover:text-white relative">
                Open the builder <ArrowRight size={12} />
              </div>
            </Link>

            {/* THREADS — slot 05 · the signal board */}
            <Link
              to="/threads"
              data-testid="pillar-threads"
              className="group flex flex-col p-8 lg:p-10 border-b lg:border-r border-white/10 bg-[#0F0F0F] hover:bg-[#141414] transition-colors relative overflow-hidden"
            >
              <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full border border-[#60a5fa]/20 opacity-40 group-hover:opacity-70 group-hover:scale-110 transition-all duration-500 pointer-events-none" />
              <div className="absolute -right-12 -top-12 w-52 h-52 rounded-full border border-[#60a5fa]/15 opacity-30 group-hover:opacity-60 pointer-events-none" />

              <div className="flex items-center justify-between mb-6 relative">
                <div className="font-mono text-[10px] tracking-[0.25em] text-[#60a5fa]">05 / THREADS</div>
                <MessageSquare size={18} className="text-[#60a5fa]" />
              </div>
              <h3 className="font-heading font-black uppercase tracking-tight text-2xl text-white mb-3 group-hover:text-[#60a5fa] transition-colors relative">
                The Signal Board
              </h3>
              <p className="text-sm text-zinc-400 leading-relaxed mb-5 relative">
                Post theories, drop receipts, float facts you can&apos;t unsee. The
                community feed for everyone — post under your name or stay invisible per thread.
              </p>

              <ul className="space-y-2.5 mb-5 relative" data-testid="threads-feature-list">
                <GameLine icon={Eye}        color="#60a5fa" label="Anon or handle"    desc="Post under your name or stay invisible · per thread" />
                <GameLine icon={AtSign}     color="#60a5fa" label="@-mention anyone"  desc="Tag members · auto-link to their profile" />
                <GameLine icon={UserPlus}   color="#60a5fa" label="Follow feed"       desc="See the newest dives from people you trust" />
                <GameLine icon={Sparkles}   color="#60a5fa" label="Stitches tab"      desc="User-built claim ribbons · follow + tip authors" />
                <GameLine icon={MailX}      color="#60a5fa" label="Burner DMs"        desc="Vanish-on-read or timer-burn · zero log" />
              </ul>

              <HighlightBox icon={Flame} color="#60a5fa" label="Hot · New · Top sort"
                body={<>Hot weights recency and reactions, Top sorts by all-time score, New is raw chronology — three lenses on the same feed.</>} />

              <div className="pt-2 font-mono text-xs uppercase tracking-[0.2em] text-[#60a5fa] inline-flex items-center gap-2 group-hover:text-white relative">
                Open the board <ArrowRight size={12} />
              </div>
            </Link>

            {/* THE TIES — slot 06 · live pin & string */}
            <Link
              to="/warroom"
              data-testid="pillar-warroom"
              className="group flex flex-col p-8 lg:p-10 border-b border-white/10 bg-[#0F0F0F] hover:bg-[#141414] transition-colors relative overflow-hidden"
            >
              <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full border border-[#22d3ee]/20 opacity-40 group-hover:opacity-70 group-hover:scale-110 transition-all duration-500 pointer-events-none" />
              <div className="absolute -right-12 -top-12 w-52 h-52 rounded-full border border-[#22d3ee]/15 opacity-30 group-hover:opacity-60 pointer-events-none" />

              <div className="flex items-center justify-between mb-6 relative">
                <div className="font-mono text-[10px] tracking-[0.25em] text-[#22d3ee]">06 / THE TIES</div>
                <Users size={18} className="text-[#22d3ee]" />
              </div>
              <h3 className="font-heading font-black uppercase tracking-tight text-2xl text-white mb-3 group-hover:text-[#22d3ee] transition-colors relative">
                Live Pin &amp; String
              </h3>
              <p className="text-sm text-zinc-400 leading-relaxed mb-5 relative">
                Live boards for every way you investigate. Collaborators sync in real
                time with cursors, chat, and a voice talk-room.
              </p>

              <ul className="space-y-2.5 mb-5 relative" data-testid="ties-feature-list">
                <GameLine icon={Pin}        color="#22d3ee" label="8 board formats"  desc="Ties · Family · Map · Timeline · Whiteboard · Forensic · Pros/Cons · Great Awakening Map" />
                <GameLine icon={FileSearch} color="#22d3ee" label="Forensic Case File" desc="Photo uploads · tape / redact / stamp overlays · printable" />
                <GameLine icon={GitBranch}  color="#22d3ee" label="Evidence strength" desc="Tag links Confirmed → Strong → Weak → Rumor" />
                <GameLine icon={Mic}        color="#22d3ee" label="10 live members"  desc="Real-time cursors · chat sidebar · WebRTC voice room" />
                <GameLine icon={Lock}       color="#22d3ee" label="Public / Invite / Solo" desc="Choose visibility · post anon or under your handle" />
              </ul>

              <HighlightBox icon={Megaphone} color="#22d3ee" label="Publish & Close"
                body={<>When the case is solved, hit PUBLISH &amp; CLOSE and the finished board drops straight into the Archive — sealed, citable, and readable forever.</>} />

              <div className="pt-2 font-mono text-xs uppercase tracking-[0.2em] text-[#22d3ee] inline-flex items-center gap-2 group-hover:text-white relative">
                Tie the threads <ArrowRight size={12} />
              </div>
            </Link>

            {/* VAULT CARDS — slot 07 · collectible card game */}
            <Link
              to="/vault-cards"
              data-testid="pillar-vault-cards"
              className="group flex flex-col p-8 lg:p-10 border-b lg:border-r border-white/10 bg-[#0F0F0F] hover:bg-[#141414] transition-colors relative overflow-hidden"
            >
              <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full border border-[#ff9500]/20 opacity-40 group-hover:opacity-70 group-hover:scale-110 transition-all duration-500 pointer-events-none" />
              <div className="absolute -right-12 -top-12 w-52 h-52 rounded-full border border-[#ff9500]/15 opacity-30 group-hover:opacity-60 pointer-events-none" />

              <div className="flex items-center justify-between mb-6 relative">
                <div className="font-mono text-[10px] tracking-[0.25em] text-[#ff9500]">07 / VAULT CARDS</div>
                <Layers size={18} className="text-[#ff9500]" />
              </div>
              <h3 className="font-heading font-black uppercase tracking-tight text-2xl text-white mb-3 group-hover:text-[#ff9500] transition-colors relative">
                Collect The Files
              </h3>
              <p className="text-sm text-zinc-400 leading-relaxed mb-5 relative">
                Brutalist file cards for real pieces of hidden history. AI-painted
                artwork on every tier, new drops every Monday, and a rainbow of
                rarities that burn out forever once minted.
              </p>

              <ul className="space-y-2.5 mb-5 relative" data-testid="vault-cards-feature-list">
                <GameLine icon={Trophy}   color="#ff9500" label="Daily free pull"     desc="One pull per UTC day · randomized rarity per user" />
                <GameLine icon={Dice5}    color="#ff9500" label="Coin dice rolls"     desc="Standard 50c · Premium 100c · Elite 300c · Transcendent 500c · Omnipotent 1,000c · 3 cards each" />
                <GameLine icon={Lock}     color="#ff9500" label="Classified · 100 copies" desc="Hard cap · Elite rolls only · sells out forever" />
                <GameLine icon={Sparkles} color="#ffd700" label="Divine · 25 copies" desc="GOLD top tier · minted from the weekly Iceberg expedition" />
                <GameLine icon={Sparkles} color="#d946ef" label="Celestial · 10 copies" desc="PRISM apex tier · 30-day Ascension + Awakening rituals" />
                <GameLine icon={Repeat}   color="#ff9500" label="Shred 5 → bonus pull" desc="Trash dupes for a weekly extra roll · lock favorites" />
              </ul>

              <HighlightBox icon={Award} color="#ff9500" label="New drops every Monday"
                body={<>Fresh cards across every rarity rotate in weekly. Limited copies plus permanent burnout means today&apos;s common could be tomorrow&apos;s holy grail.</>} />

              <div className="pt-2 font-mono text-xs uppercase tracking-[0.2em] text-[#ff9500] inline-flex items-center gap-2 group-hover:text-white relative">
                Enter the collection room <ArrowRight size={12} />
              </div>
            </Link>

            {/* THE GREAT AWAKENING — slot 08 · 5-in-1 daily game hub */}
            <Link
              to="/awakening"
              data-testid="pillar-awakening"
              className="group flex flex-col p-8 lg:p-10 border-b lg:border-b-0 lg:border-r border-white/10 bg-[#0F0F0F] hover:bg-[#141414] transition-colors relative overflow-hidden"
            >
              {/* Concentric all-seeing-eye rings — subtle backdrop */}
              <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full border border-[#fde047]/20 opacity-40 group-hover:opacity-70 group-hover:scale-110 transition-all duration-500 pointer-events-none" />
              <div className="absolute -right-12 -top-12 w-52 h-52 rounded-full border border-[#fde047]/15 opacity-30 group-hover:opacity-60 pointer-events-none" />

              <div className="flex items-center justify-between mb-6 relative">
                <div className="font-mono text-[10px] tracking-[0.25em] text-[#fde047]">08 / GREAT AWAKENING</div>
                <Gamepad2 size={18} className="text-[#fde047]" />
              </div>
              <h3 className="font-heading font-black uppercase tracking-tight text-2xl text-white mb-3 group-hover:text-[#fde047] transition-colors relative">
                The Great Awakening
              </h3>
              <p className="text-sm text-zinc-400 leading-relaxed mb-5 relative">
                A game hub for the curious. One cinematic descent, daily puzzles, and
                a weekly <span className="text-[#ffd700]">Iceberg</span> expedition. Every solve drips coins,
                mints a rarity-weighted Vault Card, and counts toward your
                <span className="text-[#fde047]"> streak</span>.
              </p>

              <ul className="space-y-2.5 mb-5 relative" data-testid="awakening-game-list">
                <GameLine icon={ArrowDown} color="#fde047" label="Rabbit Hole" desc="10-level cinematic descent · 60% Mythic / 30% Classified / 10% Divine on full clear" />
                <GameLine icon={Shuffle}   color="#fde047" label="Anagram"     desc="Unjumble today's term · 79-word themed pool" />
                <GameLine icon={Search}    color="#fde047" label="Wordsearch"  desc="Find 8 hidden words on a 12×12 themed grid · 12 themes" />
                <GameLine icon={Grid3X3}   color="#fde047" label="Crossword"   desc="Real interlocking themed mini · across + down clues" />
                <GameLine icon={Link2}     color="#fde047" label="Word Chain"  desc="Build the ladder · one letter changes per step" />
                <GameLine icon={Snowflake} color="#ffd700" label="The Iceberg" desc="WEEKLY · 5 tiers · 35 dossiers · Bedrock clear mints a Divine card" />
              </ul>

              <HighlightBox icon={Flame} color="#fde047" label="Streak rewards"
                body={<>Coins and a card on every solve. The longer the streak, the bigger the bonus — full breakdown lives in the Coin Key.</>} />

              <div className="pt-2 font-mono text-xs uppercase tracking-[0.2em] text-[#fde047] inline-flex items-center gap-2 group-hover:text-white relative">
                Enter the hub <ArrowRight size={12} />
              </div>
            </Link>

            {/* PROFILE / IDENTITY — slot 09.
                Signed-in users land on their profile. Signed-out users open the
                auth modal instead of a dead /profile redirect. */}
            <Link
              to="/profile"
              onClick={(e) => {
                if (!user) {
                  e.preventDefault();
                  window.dispatchEvent(new CustomEvent("open-auth", { detail: { mode: "signup" } }));
                }
              }}
              data-testid="pillar-profile"
              className="group flex flex-col p-8 lg:p-10 bg-[#0F0F0F] hover:bg-[#141414] transition-colors relative overflow-hidden"
            >
              <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full border border-[#a78bfa]/20 opacity-40 group-hover:opacity-70 group-hover:scale-110 transition-all duration-500 pointer-events-none" />
              <div className="absolute -right-12 -top-12 w-52 h-52 rounded-full border border-[#a78bfa]/15 opacity-30 group-hover:opacity-60 pointer-events-none" />

              <div className="flex items-center justify-between mb-6 relative">
                <div className="font-mono text-[10px] tracking-[0.25em] text-[#a78bfa]">09 / IDENTITY</div>
                <Crown size={18} className="text-[#a78bfa]" />
              </div>
              <h3 className="font-heading font-black uppercase tracking-tight text-2xl text-white mb-3 group-hover:text-[#a78bfa] transition-colors relative">
                Your File in the Vault
              </h3>
              <p className="text-sm text-zinc-400 leading-relaxed mb-5 relative">
                Sign in once and your saves, dives, connections, sigils, follows,
                and burner DMs follow you to every device. Make the space yours
                with custom themes, AI-painted backgrounds, audio frequencies,
                and a bento-grid public profile.
              </p>

              <ul className="space-y-2.5 mb-5 relative" data-testid="identity-feature-list">
                <GameLine icon={IdCard}     color="#a78bfa" label="Doxx Card"          desc="One-of-one Classified card · AI-painted from your prompt · use as avatar" />
                <GameLine icon={Award}      color="#a78bfa" label="103 sigils"         desc="Server-awarded · descriptions hidden until you unlock them" />
                <GameLine icon={Crown}      color="#a78bfa" label="Vault Rank ladder"  desc="Initiate → Archivist → Operator → Custodian" />
                <GameLine icon={Eye}        color="#a78bfa" label="Customizable profile" desc="213 themes · AI backgrounds · audio frequencies · bento grid · pinned sigil/dive/board" />
                <GameLine icon={Sparkles}   color="#ec4899" label="Meme Studio"          desc="Multi-panel canvas · 32 fonts · stamps · bubbles · publish to gallery · earn coins on remixes" />
                <GameLine icon={MailX}      color="#a78bfa" label="Burner DMs"         desc="@-mention anyone · vanish-on-read or timer-burn" />
              </ul>

              <HighlightBox icon={KeyRound} color="#a78bfa" label="Zero personal data"
                body={<>No email, no phone, no third-party trackers. Just a username, password, and recovery code you keep safe. Run multiple pseudonyms from one account.</>} />

              <div className="pt-2 font-mono text-xs uppercase tracking-[0.2em] text-[#a78bfa] inline-flex items-center gap-2 group-hover:text-white relative">
                {user ? <>View your profile <ArrowRight size={12} /></> : <>Create a free account <ArrowRight size={12} /></>}
              </div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function GameLine({ icon: Icon, label, color = "#fde047" }) {
  return (
    <li className="flex items-center gap-3">
      <Icon size={18} style={{ color }} className="shrink-0" />
      <span className="font-heading uppercase tracking-tight text-white text-[15px]">{label}</span>
    </li>
  );
}

function HighlightBox({ icon: Icon, color, label, body }) {
  return (
    <div
      className="mt-auto px-3 py-2.5 mb-3 relative border bg-[#0a0a0a] min-h-[120px] flex flex-col"
      style={{ borderColor: `${color}40` }}
    >
      <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase" style={{ color }}>
        <Icon size={12} /> {label}
      </div>
      <div className="font-mono text-[10px] tracking-[0.18em] text-zinc-400 mt-1 leading-relaxed">
        {body}
      </div>
    </div>
  );
}

